import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDynamicRowHeight, useListRef } from 'react-window';
import { FiVideo, FiPhone, FiUsers, FiSend, FiSmile, FiPaperclip, FiX, FiUserPlus, FiUserMinus, FiSearch } from 'react-icons/fi';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { useShallowStore } from '../../hooks/useShallowStore';
import { getSocket } from '../../services/socket';
import Message from './Message.jsx';
import FileUploadModal from './FileUploadModal.jsx';
import VirtualizedList from '../common/VirtualizedList.jsx';
import { SkeletonChatMessages } from '../common/Skeleton.jsx';
import api from '../../services/api';
import { toast } from 'react-toastify';
import './ChatArea.css';

const MANAGE_ROLES = ['ceo', 'co_ceo', 'company_manager', 'sales_head', 'manager'];

const chatAreaSelector = (state) => ({
  currentChannel: state.currentChannel,
  messages: state.messages,
  sendMessage: state.sendMessage,
  addMessage: state.addMessage,
  loading: state.messagesLoading,
});

function TypingIndicator({ channelId }) {
  const typingUser = useChatStore((state) => state.typingUsers[channelId]);
  if (!typingUser) return null;
  return (
    <div className="typing-indicator">
      <span>{typingUser.username} is typing...</span>
    </div>
  );
}

function OnlineStatusBadge({ memberId }) {
  const isOnline = useChatStore((state) => memberId ? state.onlineUsers.includes(memberId) : false);
  return isOnline ? (
    <span style={{ color: '#2eb67d', display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: '#2eb67d',
        display: 'inline-block'
      }}></span>
      Online
    </span>
  ) : (
    <span style={{ color: 'var(--text-secondary)' }}>Offline</span>
  );
}

function ChatArea() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { currentChannel, messages, sendMessage, addMessage, loading } = useShallowStore(useChatStore, chatAreaSelector);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const inputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const listRef = useListRef(null);
  const messageRowHeight = useDynamicRowHeight({ defaultRowHeight: 68 });

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messages.length > 0) {
      listRef.current?.scrollToRow({ index: messages.length - 1, align: 'end', behavior });
    }
  }, [messages.length, listRef]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Also scroll to bottom when component mounts or channel changes
  useEffect(() => {
    scrollToBottom('auto');
  }, [currentChannel, scrollToBottom]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
    }
  }, [messageInput]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Join channel room when currentChannel changes
  useEffect(() => {
    if (currentChannel) {
      const socket = getSocket();
      if (socket && socket.connected) {
        console.log('Joining channel room:', currentChannel._id);
        socket.emit('channel:join', currentChannel._id);
        
        // Cleanup: leave channel when component unmounts or channel changes
        return () => {
          console.log('Leaving channel room:', currentChannel._id);
          socket.emit('channel:leave', currentChannel._id);
        };
      }
    }
  }, [currentChannel]);

  useEffect(() => {
    if (channelId && currentChannel?._id !== channelId) {
      // Fetch channel data if needed
    }
  }, [channelId, currentChannel]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !currentChannel || sending) return;

    const messageText = messageInput.trim();
    setMessageInput(''); // Clear input immediately
    setSending(true);
    setShowEmojiPicker(false);

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    
    try {
      await sendMessage(currentChannel._id, messageText, 'text', [], replyingTo?._id);
      setReplyingTo(null); // Clear reply after sending
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Deliberately does NOT close the picker after a pick — closing on every
  // single emoji forced re-opening it for each one, which is what "keeps
  // ending/closing on selecting one emoji" complaints were about. It now
  // stays open (like Slack/Discord) until the user dismisses it explicitly:
  // the smiley toggle, the picker's own close button, sending the message,
  // or a click outside.
  const handleEmojiSelect = (emoji) => {
    const textarea = inputRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = messageInput;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newText = before + emoji.native + after;

    setMessageInput(newText);

    // Set cursor position after emoji
    setTimeout(() => {
      const newCursorPos = start + emoji.native.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleReply = useCallback((message) => {
    setReplyingTo(message);
    inputRef.current?.focus();
  }, []);

  const messageRowKey = useCallback((message) => message._id, []);

  const renderMessageRow = useCallback((message, index, style) => (
    <div style={style}>
      <Message
        message={message}
        isOwn={message.sender?._id === user?.id}
        showAvatar={
          index === 0 ||
          messages[index - 1]?.sender?._id !== message.sender?._id
        }
        onReply={handleReply}
      />
    </div>
  ), [messages, user, handleReply]);

  const canManageMembers = MANAGE_ROLES.includes(user?.role) && currentChannel?.type === 'project';

  // Filtering allUsers happened inline in JSX on every render (including
  // every keystroke in the message textarea, unrelated to this panel at
  // all), so it's memoized to only recompute when the actual inputs change.
  const currentMemberIds = useMemo(
    () => new Set((currentChannel?.members || []).map(m => String(m._id))),
    [currentChannel?.members]
  );
  const nonMemberUsers = useMemo(
    () => allUsers.filter(u => !currentMemberIds.has(String(u._id))),
    [allUsers, currentMemberIds]
  );
  const addableMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return nonMemberUsers;
    return nonMemberUsers.filter(u => `${u.firstName} ${u.lastName} ${u.username}`.toLowerCase().includes(q));
  }, [nonMemberUsers, memberSearch]);

  const handleOpenMembers = async () => {
    setShowMembers(true);
    if (canManageMembers && allUsers.length === 0) {
      try {
        const res = await api.get('/users');
        // exclude clients
        setAllUsers(res.data.users.filter(u => u.role !== 'client'));
      } catch { toast.error('Failed to load users'); }
    }
  };

  const handleAddMember = async (userId) => {
    try {
      await api.post(`/channels/${currentChannel._id}/members`, { userIds: [userId] });
      toast.success('Member added');
      // refresh channel
      const res = await api.get(`/channels/${currentChannel._id}`);
      useChatStore.setState(state => ({
        channels: state.channels.map(c => c._id === currentChannel._id ? res.data.channel : c),
        currentChannel: res.data.channel
      }));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to add member'); }
  };

  const handleRemoveMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from this channel?`)) return;
    try {
      await api.delete(`/channels/${currentChannel._id}/members/${userId}`);
      toast.success('Member removed');
      const res = await api.get(`/channels/${currentChannel._id}`);
      useChatStore.setState(state => ({
        channels: state.channels.map(c => c._id === currentChannel._id ? res.data.channel : c),
        currentChannel: res.data.channel
      }));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to remove member'); }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleTyping = () => {
    const socket = getSocket();
    if (socket && currentChannel) {
      socket.emit('typing:start', { channelId: currentChannel._id });
    }
  };

  const handleStopTyping = () => {
    const socket = getSocket();
    if (socket && currentChannel) {
      socket.emit('typing:stop', { channelId: currentChannel._id });
    }
  };

  const startCall = (callType) => {
    const socket = getSocket();
    console.log('Starting call - Socket connected:', socket?.connected);
    console.log('Current channel:', currentChannel);
    console.log('Call type:', callType);
    
    if (!socket) {
      alert('Socket not connected. Please refresh the page.');
      return;
    }
    
    if (!socket.connected) {
      alert('Socket disconnected. Please refresh the page.');
      return;
    }
    
    if (currentChannel) {
      console.log('Emitting call:initiate and navigating...');
      socket.emit('call:initiate', {
        channelId: currentChannel._id,
        callType
      });
      
      // Navigate to call page
      const callPath = `/dashboard/call/${currentChannel._id}?type=${callType}`;
      console.log('Navigating to:', callPath);
      navigate(callPath);
    } else {
      alert('No channel selected');
    }
  };

  if (!currentChannel) {
    return (
      <div className="chat-area-empty">
        <div className="empty-state">
          <h2>Welcome to Bigwals</h2>
          <p>Select a channel from the sidebar to start chatting</p>
        </div>
      </div>
    );
  }

  // Get current user ID consistently
  const currentUserId = String(user?._id || user?.id || '');

  // Get channel display name and other member for direct messages
  let channelDisplayName = currentChannel.name;
  let otherMember = null;

  if (currentChannel.type === 'direct') {
    // Find the other member in DM
    otherMember = currentChannel.members?.find(m => {
      const memberId = String(m._id || m.id || '');
      return memberId !== currentUserId && memberId !== '';
    });

    if (otherMember) {
      channelDisplayName = `${otherMember.firstName || ''} ${otherMember.lastName || ''}`.trim() || otherMember.username || 'Unknown User';
    }
  }

  const otherMemberId = otherMember ? String(otherMember._id || otherMember.id) : null;

  return (
    <div className="chat-area">
      <div className="chat-header">
        <div className="channel-info">
          <h2>
            {currentChannel.type === 'direct' ? (
              <>
                {channelDisplayName}
              </>
            ) : (
              `# ${channelDisplayName}`
            )}
          </h2>
          {currentChannel.type === 'direct' ? (
            <p>
              <OnlineStatusBadge memberId={otherMemberId} />
            </p>
          ) : (
            currentChannel.description && <p>{currentChannel.description}</p>
          )}
        </div>
        <div className="chat-actions">
          <button
            className="action-btn"
            onClick={() => startCall('audio')}
            title="Start voice call"
          >
            <FiPhone />
          </button>
          <button
            className="action-btn"
            onClick={() => startCall('video')}
            title="Start video call"
          >
            <FiVideo />
          </button>
          {currentChannel.type !== 'direct' && (
            <button className="action-btn" title="Channel members" onClick={handleOpenMembers}>
              <FiUsers />
              <span>{currentChannel.members?.length || 0}</span>
            </button>
          )}
        </div>
      </div>

      <div className="messages-container scrollbar-thin">
        {loading ? (
          <SkeletonChatMessages />
        ) : messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <VirtualizedList
            items={messages}
            itemKey={messageRowKey}
            rowHeight={messageRowHeight}
            renderRow={renderMessageRow}
            listRef={listRef}
            className="messages-virtual-list scrollbar-thin"
            style={{ height: '100%' }}
          />
        )}
        <TypingIndicator channelId={currentChannel._id} />
      </div>

      <div className="message-input-container">
        {replyingTo && (
          <div className="reply-preview-container">
            <div className="reply-preview-content">
              <div className="reply-preview-header">
                <span className="reply-preview-label">Replying to {replyingTo.sender?.firstName} {replyingTo.sender?.lastName}</span>
                <button 
                  className="reply-cancel-btn"
                  onClick={cancelReply}
                  type="button"
                >
                  <FiX />
                </button>
              </div>
              <div className="reply-preview-text">
                {replyingTo.content || 'Attachment'}
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSendMessage} className="message-form">
          <button
            type="button"
            className="emoji-btn"
            onClick={() => { setShowFileUpload(true); setShowEmojiPicker(false); }}
            title="Attach file"
          >
            <FiPaperclip />
          </button>
          <div className="emoji-picker-container" ref={emojiPickerRef}>
            <button
              type="button"
              className="emoji-btn"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              title="Add emoji"
            >
              <FiSmile />
            </button>
            {showEmojiPicker && (
              <div className="emoji-picker-wrapper">
                <div className="emoji-picker-header">
                  <span>Pick an emoji</span>
                  <button
                    type="button"
                    className="emoji-picker-close-btn"
                    onClick={() => setShowEmojiPicker(false)}
                    title="Close"
                  >
                    <FiX />
                  </button>
                </div>
                <Picker
                  data={data}
                  onEmojiSelect={handleEmojiSelect}
                  theme="light"
                  previewPosition="none"
                  skinTonePosition="none"
                />
              </div>
            )}
          </div>
          <textarea
            ref={inputRef}
            placeholder={`Message ${currentChannel.type === 'direct' ? channelDisplayName : '#' + currentChannel.name}`}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyPress={handleTyping}
            onBlur={handleStopTyping}
            disabled={sending}
            rows={1}
          />
          <button
            type="submit"
            className="send-btn"
            disabled={!messageInput.trim() || sending}
          >
            {sending ? '...' : <FiSend />}
          </button>
        </form>
      </div>

      {showFileUpload && (
        <FileUploadModal
          channelId={currentChannel._id}
          onClose={() => setShowFileUpload(false)}
          onFileUploaded={(message) => {
            addMessage(message);
            setShowFileUpload(false);
          }}
        />
      )}

      {/* Members Panel */}
      {showMembers && (
        <div className="members-panel-overlay" onClick={() => setShowMembers(false)}>
          <div className="members-panel" onClick={e => e.stopPropagation()}>
            <div className="members-panel-header">
              <h3><FiUsers /> Members ({currentChannel.members?.length || 0})</h3>
              <button className="members-panel-close" onClick={() => setShowMembers(false)}><FiX /></button>
            </div>

            {/* Current Members */}
            <div className="members-panel-section">
              <h4>Current Members</h4>
              <div className="members-panel-list">
                {currentChannel.members?.map(m => (
                  <div key={m._id} className="member-panel-item">
                    <div className="member-panel-avatar">{m.firstName?.[0]}{m.lastName?.[0]}</div>
                    <div className="member-panel-info">
                      <span className="member-panel-name">{m.firstName} {m.lastName}</span>
                      <span className="member-panel-role">{m.role?.replace(/_/g, ' ')}</span>
                    </div>
                    {canManageMembers && String(m._id) !== String(user?._id) && (
                      <button
                        className="member-remove-btn"
                        onClick={() => handleRemoveMember(m._id, `${m.firstName} ${m.lastName}`)}
                        title="Remove from channel"
                      >
                        <FiUserMinus />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Add Members — only for authorized roles on project channels */}
            {canManageMembers && (
              <div className="members-panel-section">
                <h4><FiUserPlus /> Add Member</h4>
                <div className="members-search-box">
                  <FiSearch />
                  <input
                    placeholder="Search employees..."
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                  />
                </div>
                <div className="members-panel-list">
                  {addableMembers.map(u => (
                    <div key={u._id} className="member-panel-item">
                      <div className="member-panel-avatar">{u.firstName?.[0]}{u.lastName?.[0]}</div>
                      <div className="member-panel-info">
                        <span className="member-panel-name">{u.firstName} {u.lastName}</span>
                        <span className="member-panel-role">{u.role?.replace(/_/g, ' ')}</span>
                      </div>
                      <button
                        className="member-add-btn"
                        onClick={() => handleAddMember(u._id)}
                        title="Add to channel"
                      >
                        <FiUserPlus />
                      </button>
                    </div>
                  ))}
                  {nonMemberUsers.length === 0 && (
                    <p className="members-empty">All employees are already members</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatArea;
